import { useT } from '@kmgestao/i18n';
import { Badge, Button, Skeleton, toast } from '@kmgestao/ui';

import { useSession } from '../../../features/auth/index.js';
import {
  useClinicTeamMembers,
  useReactivateMember,
  useSuspendMember,
  type ClinicMemberRow,
} from '../../../features/clinic-team/index.js';

export interface TeamMembersTableProps {
  clinicId: string;
}

/**
 * Tabela de membros da clinic. Cada linha mostra nome + email + papel
 * + status + ações (suspender/reativar).
 *
 * Ações por status:
 *   - `active` (não-owner) → botão "Suspender"
 *   - `active` (owner) → sem botão (owner não pode ser suspendido pela
 *     própria UI; ação é exclusiva da Karla via banco)
 *   - `invited` → mostra "Aguardando ativação" + reusa fluxo de invite
 *     (sem botão de reenviar nesta PR — postergado pra simplicidade)
 *   - `suspended` → botão "Reativar"
 *
 * Owner não pode se suspender mesmo via banco (RLS poderia, mas UI
 * bloqueia explicitamente). Caller=owner vê seu próprio row read-only.
 */
export function TeamMembersTable({ clinicId }: TeamMembersTableProps) {
  const { t } = useT('clinics');
  const { session } = useSession();
  const currentUserId = session?.user?.id;
  const membersQ = useClinicTeamMembers(clinicId);

  if (membersQ.isLoading) {
    return <TableSkeleton />;
  }

  if (membersQ.isError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {t('team.list.loadError', {
          message: membersQ.error instanceof Error ? membersQ.error.message : '',
        })}
      </p>
    );
  }

  const members = membersQ.data ?? [];
  if (members.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('team.list.empty')}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="p-2 font-medium">{t('team.list.columns.name')}</th>
            <th className="p-2 font-medium">{t('team.list.columns.email')}</th>
            <th className="p-2 font-medium">{t('team.list.columns.role')}</th>
            <th className="p-2 font-medium">{t('team.list.columns.status')}</th>
            <th className="p-2 font-medium text-right">
              <span className="sr-only">{t('team.list.columns.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              clinicId={clinicId}
              isCurrentUser={m.user_id === currentUserId}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MemberRow({
  member,
  clinicId,
  isCurrentUser,
}: {
  member: ClinicMemberRow;
  clinicId: string;
  isCurrentUser: boolean;
}) {
  const { t } = useT('clinics');
  const suspendMutation = useSuspendMember();
  const reactivateMutation = useReactivateMember();
  const isOwner = member.role_key === 'clinic_owner';
  const isPending = suspendMutation.isPending || reactivateMutation.isPending;

  const handleSuspend = async () => {
    try {
      await suspendMutation.mutateAsync({ clinic_id: clinicId, member_id: member.id });
      toast.success(t('team.list.actions.suspendSuccess'));
    } catch (err) {
      toast.error(t('team.list.actions.suspendError'), {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateMutation.mutateAsync({ clinic_id: clinicId, member_id: member.id });
      toast.success(t('team.list.actions.reactivateSuccess'));
    } catch (err) {
      toast.error(t('team.list.actions.reactivateError'), {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <tr className="border-b border-border">
      <td className="p-2 font-medium text-foreground">
        {member.profile?.full_name ?? '—'}
        {isCurrentUser ? (
          <span className="ml-2 text-xs text-muted-foreground">({t('team.list.youSuffix')})</span>
        ) : null}
      </td>
      <td className="p-2 break-all text-muted-foreground">{member.profile?.email ?? '—'}</td>
      <td className="p-2">
        <Badge variant="outline">
          {t(`team.list.roleLabels.${member.role_key}`, { defaultValue: member.role_key })}
        </Badge>
      </td>
      <td className="p-2">
        <Badge variant={statusBadgeVariant(member.status)}>
          {t(`team.list.statusLabels.${member.status}`, { defaultValue: member.status })}
        </Badge>
      </td>
      <td className="p-2 text-right">
        {/* Owner: read-only — só Karla muda via banco */}
        {isOwner ? (
          <span className="text-xs text-muted-foreground">{t('team.list.ownerImmutable')}</span>
        ) : isCurrentUser ? (
          // Caller olhando o próprio row — não pode se auto-suspender
          <span className="text-xs text-muted-foreground">{t('team.list.cannotSelfSuspend')}</span>
        ) : member.status === 'active' || member.status === 'invited' ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isPending}
            onClick={handleSuspend}
          >
            {t('team.list.actions.suspend')}
          </Button>
        ) : member.status === 'suspended' ? (
          <Button type="button" size="sm" disabled={isPending} onClick={handleReactivate}>
            {t('team.list.actions.reactivate')}
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

function TableSkeleton() {
  const { t } = useT('clinics');
  return (
    <div className="space-y-2">
      <Skeleton className="h-5 w-1/3" />
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-11 w-full" />
      ))}
      <span className="sr-only">{t('team.list.loading')}</span>
    </div>
  );
}

function statusBadgeVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'success' | 'outline' {
  switch (status) {
    case 'active':
      return 'success';
    case 'invited':
      // Badge não tem variant 'warning' — usamos 'secondary' (cor neutra-muted)
      // pra distinguir de 'destructive' (suspended) e 'success' (active).
      return 'secondary';
    case 'suspended':
      return 'destructive';
    default:
      return 'outline';
  }
}
